import { create } from 'zustand';
import { PITCH_TYPES, calculateHitResult, getRank } from './baseballConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useBaseballStore = create((set, get) => ({
  // Game state
  gameMode: null, // 'derby', 'duel', null
  gameStatus: 'idle', // idle, waiting, playing, ended
  
  // Derby mode state
  derbyRound: 0,
  derbyPitchesLeft: 10,
  derbyHomeRuns: 0,
  derbyTotalDistance: 0,
  derbyLongestHR: 0,
  derbyResults: [],
  
  // Duel mode state
  duelRole: null, // 'pitcher' or 'batter'
  duelInning: 1,
  duelOuts: 0,
  duelScore: { player: 0, opponent: 0 },
  isMyTurn: false,
  
  // Current at-bat state
  currentPitch: null,
  pitchInFlight: false,
  pitchPosition: null,
  swingResult: null,
  
  // Player stats & progression
  playerStats: {
    gamesPlayed: 0,
    wins: 0,
    losses: 0,
    homeRuns: 0,
    strikeouts: 0,
    battingAverage: 0,
    rankedPoints: 0
  },
  achievements: [],
  unlockedPitches: ['FASTBALL', 'CURVEBALL', 'SLIDER', 'CHANGEUP'],
  powerUps: {
    powerSwing: 0,
    batSizeBonus: 1.0,
    contactBoost: 1.0
  },
  
  // Multiplayer
  roomId: null,
  opponentName: null,
  wsConnection: null,
  
  // UI state
  showResult: false,
  lastHitResult: null,
  
  // ============ DERBY MODE ============
  
  startDerby: () => {
    set({
      gameMode: 'derby',
      gameStatus: 'playing',
      derbyRound: 1,
      derbyPitchesLeft: 10,
      derbyHomeRuns: 0,
      derbyTotalDistance: 0,
      derbyLongestHR: 0,
      derbyResults: [],
      currentPitch: null,
      pitchInFlight: false,
      swingResult: null,
      showResult: false
    });
  },
  
  throwDerbyPitch: () => {
    const { derbyPitchesLeft } = get();
    if (derbyPitchesLeft <= 0) return;
    
    // Derby uses medium fastballs down the middle
    const pitch = {
      type: 'FASTBALL',
      ...PITCH_TYPES.FASTBALL,
      targetX: (Math.random() - 0.5) * 0.5, // Slight variation
      targetY: 2.5 + (Math.random() - 0.5) * 0.3,
      startTime: Date.now()
    };
    
    set({
      currentPitch: pitch,
      pitchInFlight: true,
      pitchPosition: { x: 0, y: 2, z: -18 }
    });
  },
  
  swingBat: (timing, swingAngle) => {
    const { currentPitch, powerUps, derbyPitchesLeft, derbyHomeRuns, derbyTotalDistance, derbyResults } = get();
    
    if (!currentPitch) return;
    
    const result = calculateHitResult(
      timing,
      swingAngle,
      currentPitch.type,
      false // No power swing in derby
    );
    
    // Apply bat size bonus to contact
    if (powerUps.batSizeBonus > 1 && result.result === 'STRIKE') {
      // Second chance with bonus
      if (Math.random() < (powerUps.batSizeBonus - 1) * 2) {
        result.result = 'OUT';
        result.contactQuality = 'weak';
      }
    }
    
    const newResults = [...derbyResults, result];
    const newHRs = result.result === 'HOMERUN' ? derbyHomeRuns + 1 : derbyHomeRuns;
    const newDistance = result.result === 'HOMERUN' ? derbyTotalDistance + result.distance : derbyTotalDistance;
    const newLongest = result.result === 'HOMERUN' ? Math.max(get().derbyLongestHR, result.distance) : get().derbyLongestHR;
    const pitchesLeft = derbyPitchesLeft - 1;
    
    set({
      pitchInFlight: false,
      swingResult: result,
      showResult: true,
      lastHitResult: result,
      derbyResults: newResults,
      derbyHomeRuns: newHRs,
      derbyTotalDistance: newDistance,
      derbyLongestHR: newLongest,
      derbyPitchesLeft: pitchesLeft,
      gameStatus: pitchesLeft <= 0 ? 'ended' : 'playing'
    });
    
    // Auto-hide result after animation completes (6 seconds for HR tracking)
    setTimeout(() => {
      set({ showResult: false, swingResult: null });
    }, 6000);
  },
  
  saveDerbyScore: async () => {
    const { derbyHomeRuns, derbyTotalDistance } = get();
    
    try {
      await fetch(`${API_URL}/api/baseball/derby-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          homeRuns: derbyHomeRuns,
          totalDistance: derbyTotalDistance
        })
      });
    } catch (error) {
      console.error('Failed to save derby score:', error);
    }
  },
  
  // ============ DUEL MODE (MULTIPLAYER) ============
  
  createDuelRoom: async (playerName) => {
    try {
      const response = await fetch(`${API_URL}/api/baseball/create-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ player_name: playerName })
      });
      
      const data = await response.json();
      
      set({
        roomId: data.room_id,
        gameMode: 'duel',
        gameStatus: 'waiting',
        duelRole: 'pitcher' // Creator starts as pitcher
      });
      
      // Connect to WebSocket
      get().connectToRoom(data.room_id);
      
      return data;
    } catch (error) {
      console.error('Failed to create room:', error);
      return null;
    }
  },
  
  joinDuelRoom: async (roomId, playerName) => {
    try {
      const response = await fetch(`${API_URL}/api/baseball/room/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ player_name: playerName })
      });
      
      const data = await response.json();
      
      set({
        roomId,
        gameMode: 'duel',
        gameStatus: 'playing',
        duelRole: 'batter', // Joiner starts as batter
        opponentName: data.host_name
      });
      
      // Connect to WebSocket
      get().connectToRoom(roomId);
      
      return data;
    } catch (error) {
      console.error('Failed to join room:', error);
      return null;
    }
  },
  
  connectToRoom: (roomId) => {
    const wsUrl = API_URL.replace(/^https/, 'wss').replace(/^http:/, 'ws:');
    const ws = new WebSocket(`${wsUrl}/api/ws/baseball/${roomId}`);
    
    ws.onopen = () => {
      console.log('Connected to baseball room');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      get().handleWebSocketMessage(data);
    };
    
    ws.onclose = () => {
      console.log('Disconnected from baseball room');
      set({ wsConnection: null });
    };
    
    set({ wsConnection: ws });
  },
  
  handleWebSocketMessage: (data) => {
    switch (data.type) {
      case 'player_joined':
        set({
          opponentName: data.player_name,
          gameStatus: 'playing',
          isMyTurn: get().duelRole === 'pitcher'
        });
        break;
        
      case 'pitch':
        // Opponent threw a pitch (I'm batting)
        set({
          currentPitch: data.pitch,
          pitchInFlight: true,
          pitchPosition: { x: 0, y: 2, z: -18 },
          isMyTurn: true
        });
        break;
        
      case 'swing_result':
        // Opponent swung (I was pitching)
        set({
          swingResult: data.result,
          showResult: true,
          lastHitResult: data.result,
          pitchInFlight: false
        });
        get().updateDuelScore(data.result);
        break;
        
      case 'inning_change':
        set({
          duelInning: data.inning,
          duelOuts: 0,
          duelRole: get().duelRole === 'pitcher' ? 'batter' : 'pitcher',
          isMyTurn: get().duelRole === 'batter' // Swap roles
        });
        break;
        
      case 'game_over':
        set({
          gameStatus: 'ended',
          duelScore: data.finalScore
        });
        break;
        
      default:
        break;
    }
  },
  
  sendPitch: (pitchType, targetX, targetY) => {
    const { wsConnection, duelRole } = get();
    if (!wsConnection || duelRole !== 'pitcher') return;
    
    const pitch = {
      type: pitchType,
      ...PITCH_TYPES[pitchType],
      targetX,
      targetY,
      startTime: Date.now()
    };
    
    wsConnection.send(JSON.stringify({
      type: 'pitch',
      pitch
    }));
    
    set({
      currentPitch: pitch,
      pitchInFlight: true,
      isMyTurn: false
    });
  },
  
  sendSwing: (timing, swingAngle) => {
    const { wsConnection, currentPitch, powerUps, duelRole } = get();
    if (!wsConnection || duelRole !== 'batter' || !currentPitch) return;
    
    const result = calculateHitResult(timing, swingAngle, currentPitch.type, false);
    
    wsConnection.send(JSON.stringify({
      type: 'swing',
      timing,
      swingAngle,
      result
    }));
    
    set({
      swingResult: result,
      showResult: true,
      lastHitResult: result,
      pitchInFlight: false,
      isMyTurn: false
    });
    
    get().updateDuelScore(result);
  },
  
  updateDuelScore: (result) => {
    const { duelOuts, duelScore, duelRole, duelInning } = get();
    
    if (result.result === 'STRIKE' || result.result === 'OUT') {
      const newOuts = duelOuts + 1;
      
      if (newOuts >= 3) {
        // Inning over, switch roles
        set({
          duelOuts: 0,
          duelRole: duelRole === 'pitcher' ? 'batter' : 'pitcher',
          duelInning: duelRole === 'batter' ? duelInning + 1 : duelInning
        });
      } else {
        set({ duelOuts: newOuts });
      }
    } else {
      // Hit! Add to score
      const runs = {
        SINGLE: 0.25,
        DOUBLE: 0.5,
        TRIPLE: 0.75,
        HOMERUN: 1
      }[result.result] || 0;
      
      const scoreKey = duelRole === 'batter' ? 'player' : 'opponent';
      set({
        duelScore: {
          ...duelScore,
          [scoreKey]: duelScore[scoreKey] + runs
        }
      });
    }
  },
  
  // ============ STATS & PROGRESSION ============
  
  loadPlayerStats: async () => {
    try {
      const response = await fetch(`${API_URL}/api/baseball/stats`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        set({
          playerStats: data.stats,
          achievements: data.achievements || [],
          unlockedPitches: data.unlocked_pitches || ['FASTBALL', 'CURVEBALL', 'SLIDER', 'CHANGEUP'],
          powerUps: data.power_ups || { powerSwing: 0, batSizeBonus: 1.0, contactBoost: 1.0 }
        });
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  },
  
  checkAchievement: (achievementId) => {
    const { achievements } = get();
    return achievements.some(a => a.id === achievementId);
  },
  
  // ============ CLEANUP ============
  
  resetGame: () => {
    const { wsConnection } = get();
    if (wsConnection) {
      wsConnection.close();
    }
    
    set({
      gameMode: null,
      gameStatus: 'idle',
      derbyRound: 0,
      derbyPitchesLeft: 10,
      derbyHomeRuns: 0,
      derbyTotalDistance: 0,
      derbyLongestHR: 0,
      derbyResults: [],
      duelRole: null,
      duelInning: 1,
      duelOuts: 0,
      duelScore: { player: 0, opponent: 0 },
      currentPitch: null,
      pitchInFlight: false,
      swingResult: null,
      showResult: false,
      roomId: null,
      opponentName: null,
      wsConnection: null
    });
  }
}));

export default useBaseballStore;
